import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getMe } from '@/services/me';
import { stripMd } from '@/lib/text';
import { useNav, navDisplayLabel } from '@/state/nav.store';
import { useTranslation, Trans } from 'react-i18next';
import type { Locale } from '@/i18n/config';
import { HexisSymbol } from '@marcianus/design/components';
import { Icon, EmptyState, HexisThinking } from '@marcianus/design/components';
import { ROUTES } from '@/app/routes';
import { askHexis, askHexisComProgresso, approveHexisAction } from '../services/hexis.api';
import type { HexisReply } from '@marcianus/shared/hexis-client';
import { openSignIn } from '@/lib/session';
import { env } from '@/lib/env';
import { useInbox, Briefing, InboxStatus, NotifCard } from '../components/inbox.ui';
import { ChatAvatar } from '@/components/ChatAvatar';
import { REGION_TAG } from '@/i18n/config';
import { useProfilePhoto } from '@/lib/session';

export type HexisTab = 'chat' | 'alertas';
interface Msg {
  role: 'user' | 'hexis';
  text: string;
  to?: string;
  toLabel?: string;
  // Ação proposta pelo Hexis a aguardar a tua aprovação (Human-in-the-Loop).
  pending?: { token: string; tool: string; summary: string };
  resolved?: boolean;   // já aprovada/dispensada → esconde os botões
  note?: string;        // desfecho ("Feito ✓", "Ação dispensada", erro)
}

// ── Intertravamento: a resposta do Hexis leva à TELA de que fala. O intent vem
// do cérebro (server-side); aqui só o traduzimos num destino real via ROUTES.
// As chaves `intent` e os destinos são identificadores de negócio (independentes do idioma);
// só o RÓTULO do botão vem do catálogo (hexis:intent.*).
type IntentLabel = 'events' | 'network' | 'leads' | 'wallet' | 'tickets' | 'progress' | 'profile' | 'content';
const INTENT_ROUTE: Record<string, { to: string; label: IntentLabel }> = {
  events:           { to: ROUTES.attendee.explore, label: 'events' },
  event:            { to: ROUTES.attendee.explore, label: 'events' },
  matchscore:       { to: ROUTES.tap.connections,  label: 'network' },
  match:            { to: ROUTES.tap.connections,  label: 'network' },
  networking:       { to: ROUTES.tap.connections,  label: 'network' },
  event_networking: { to: ROUTES.tap.connections,  label: 'network' },
  my_leads:         { to: ROUTES.stand,            label: 'leads' },
  wallet:           { to: ROUTES.attendee.wallet,  label: 'wallet' },
  tickets:          { to: ROUTES.attendee.tickets, label: 'tickets' },
  ticket:           { to: ROUTES.attendee.tickets, label: 'tickets' },
  gamification:     { to: ROUTES.conta,            label: 'progress' },
  xp:               { to: ROUTES.conta,            label: 'progress' },
  mission:          { to: ROUTES.conta,            label: 'progress' },
  profile:          { to: ROUTES.perfil,           label: 'profile' },
  content:          { to: ROUTES.conteudos,        label: 'content' },
};

// Janela glass FLUTUANTE sobre a tab. FAB → Chat (Hexis); sino → Alertas
// (notificações). Sem tabs: cada entrada abre a sua superfície (decisão do produto).
export default function HexisPopup({ tab, onClose }: { tab: HexisTab; onClose: () => void }) {
  const { t } = useTranslation('hexis');
  const isChat = tab === 'chat';
  return (
    <>
      <div className="sheet-backdrop open" onClick={onClose} />
      <div className="glass-popup" role="dialog" aria-label={isChat ? t('dialogChat') : t('dialogAlerts')}>
        <header className="app-header" style={{ background: 'transparent', borderBottom: '1px solid var(--border-1)', position: 'relative', zIndex: 1 }}>
          <span className="hdr-brand" style={{ background: 'transparent', border: 0 }} aria-hidden>
            {isChat ? <HexisSymbol size={22} /> : <Icon name="bell" size={20} />}
          </span>
          <div className="hdr-info">
            <span className="hdr-eyebrow">{isChat ? t('copilot') : t('realtime')}</span>
            <span className="hdr-title">{isChat ? t('title') : t('alertsTitle')}</span>
          </div>
          <button className="icon-btn" aria-label={t('close')} onClick={onClose}><Icon name="x" size={18} /></button>
        </header>

        {isChat ? <ChatView /> : <AlertasView />}
      </div>
    </>
  );
}

// ── Chat (Ollama) — contexto em tempo real + saudação + mic→campo. ──
function ChatView() {
  const { t, i18n } = useTranslation('hexis');
  const locale = i18n.language as Locale;
  const suggestFor = (tab: string): string[] => {
    const k = (tab === 'tap' || tab === 'centro' ? tab : 'eventos') as 'eventos' | 'tap' | 'centro';
    return t(`suggest.${k}`, { returnObjects: true }) as unknown as string[];
  };
  const greeting = () => {
    const h = new Date().getHours();
    return t(h < 12 ? 'greet.morning' : h < 19 ? 'greet.afternoon' : 'greet.night');
  };
  const nav = useNav();
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });
  const foto = useProfilePhoto(me.data?.avatar_url);
  const [input, setInput] = useState('');
  const [msgs, setMsgs] = useState<Msg[]>([]);
  // Passos REAIS do cérebro enquanto pensa (SSE). Não é decoração: em CPU, o
  // modelo leva 15–30 s até ao 1.º token, e três pontinhos nesse tempo não
  // distinguem «a trabalhar» de «avariou».
  const [passos, setPassos] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>(() => suggestFor(nav.tab));
  const bodyRef = useRef<HTMLDivElement>(null);
  const firstName = (me.data?.full_name || '').trim().split(' ')[0] || '';

  const navigate = useNavigate();
  const ask = useMutation({
    mutationFn: async (message: string) => {
      setPassos([]);
      const ctx = { tab: nav.tab, subtab: nav.subtab, label: nav.label };
      try {
        let final: HexisReply | null = null;
        for await (const chunk of askHexisComProgresso({ message, userId: me.data!.id, context: ctx, locale })) {
          if (chunk.type === 'step') setPassos((p) => [...p, chunk.content]);
          else if (chunk.type === 'done') final = chunk.payload;
        }
        if (final) return final;
        throw new Error('stream sem resposta');
      } catch {
        // Rede sem SSE (proxy a tamponar, browser antigo): a resposta continua
        // a chegar pelo caminho de sempre — perde-se o progresso, não a função.
        return askHexis({ message, userId: me.data!.id, context: ctx, locale });
      }
    },
    onSuccess: (res) => {
      // O intent do cérebro vira um CTA real — a conversa LEVA à tela certa.
      const dest = INTENT_ROUTE[(res.intent || '').toLowerCase()];
      const pa = res.pending_action;
      setMsgs((m) => [...m, {
        role: 'hexis', text: stripMd(res.reply ?? '') || t('noReply'),
        to: dest?.to, toLabel: dest ? t(`intent.${dest.label}`) : undefined,
        pending: pa ? { token: pa.token, tool: pa.tool, summary: pa.summary ?? '' } : undefined,
      }]);
      setSuggestions(res.suggestions ?? []);
      requestAnimationFrame(() => bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight }));
    },
    onError: () => setMsgs((m) => [...m, { role: 'hexis', text: t('cantReply') }]),
    onSettled: () => setPassos([]),
  });

  // Aprovação da ação proposta — só aqui a mutação sai do servidor (Human-in-the-Loop).
  const approve = useMutation({
    mutationFn: (vars: { token: string; idx: number }) => approveHexisAction({ token: vars.token, userId: me.data!.id }),
    onSuccess: (res, vars) => setMsgs((m) => m.map((msg, i) =>
      i === vars.idx ? { ...msg, resolved: true, note: res.ok ? t('done') : (res.error || t('cantFinish')) } : msg)),
    onError: (_e, vars) => setMsgs((m) => m.map((msg, i) =>
      i === vars.idx ? { ...msg, resolved: true, note: t('cantFinishNow') } : msg)),
  });
  function dismiss(idx: number) {
    setMsgs((m) => m.map((msg, i) => i === idx ? { ...msg, resolved: true, note: t('dismissed') } : msg));
  }

  function send(text: string) {
    const q = text.trim();
    if (!q) return;
    // Convidado: perguntar abre a criação de conta — o mesmo gancho da página.
    if (!me.data?.id) { if (!env.preview) openSignIn(); return; }
    setMsgs((m) => [...m, { role: 'user', text: q }]); setInput(''); setSuggestions([]); ask.mutate(q);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const SR: any = typeof window !== 'undefined' && ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  function startMic() {
    if (!SR) return; const rec = new SR(); rec.lang = REGION_TAG[locale];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rec.onresult = (e: any) => { const heard: string | undefined = e?.results?.[0]?.[0]?.transcript; if (heard) setInput((p) => (p ? p + ' ' : '') + heard); };
    rec.start();
  }

  return (
    <>
      <div ref={bodyRef} className="chat-msgs" role="log" aria-live="polite" style={{ position: 'relative', zIndex: 1 }}>
        {msgs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '14px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <span className="brand-badge" aria-hidden><HexisSymbol size={26} /></span>
            <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, letterSpacing: '-0.01em' }}>{firstName ? t('named', { greet: greeting(), name: firstName }) : t('plain', { greet: greeting() })}</div>
            <p style={{ color: 'var(--fg-60)', fontSize: 'var(--fs-body)', maxWidth: '30ch' }}>
              <Trans t={t} i18nKey="seeYou" values={{ where: navDisplayLabel(nav) }} components={{ b: <b style={{ color: 'var(--purple-50)' }} /> }} />
            </p>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`chat-msg${m.role === 'user' ? ' mine' : ''}`}>
            <ChatAvatar de={m.role === 'hexis' ? 'hexis' : 'eu'}
              nome={me.data?.full_name} foto={foto} />
            <div className="chat-turn">
              <div className="chat-bubble">{m.text}</div>
              {m.to && (
                <button className="chip purple" onClick={() => navigate(m.to!)}>{m.toLabel} →</button>
              )}
              {/* Duas respostas à mesma pergunta = dois BOTÕES. «Agora não» era um
                  chip ao lado de um botão: pesos diferentes para escolhas que o
                  utilizador tem de comparar. */}
              {m.pending && !m.resolved && (
                <div className="btn-row" style={{ alignSelf: 'stretch' }}>
                  <button className="btn btn-sm btn-purple" disabled={approve.isPending}
                    onClick={() => approve.mutate({ token: m.pending!.token, idx: i })}>
                    {approve.isPending ? t('confirming') : t('approve')}
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => dismiss(i)}>{t('notNow')}</button>
                </div>
              )}
              {m.note && <div className="sect-note" style={{ margin: 0 }}>{m.note}</div>}
            </div>
          </div>
        ))}
        {ask.isPending && (
          <div className="chat-msg">
            <ChatAvatar de="hexis" />
            <HexisThinking steps={passos} />
          </div>
        )}
      </div>

      {suggestions.length > 0 && (
        <div className="h-scroll" style={{ padding: '0 var(--gutter) 10px', position: 'relative', zIndex: 1 }}>
          {suggestions.map((s) => <button key={s} className="chip purple" onClick={() => send(s)}>{s}</button>)}
        </div>
      )}

      <form className="chat-input" style={{ position: 'relative', zIndex: 1 }} onSubmit={(e) => { e.preventDefault(); send(input); }}>
        {/* O botão do micro media 30px — abaixo do alvo mínimo e abaixo do
            próprio `.icon-btn` (34). Agora é o do sistema, a 40. */}
        {SR && <button type="button" className="icon-btn" style={{ width: 40, height: 40 }} aria-label={t('speak')} onClick={startMic}><Icon name="mic" size={16} /></button>}
        <input className="input" value={input} onChange={(e) => setInput(e.target.value)} aria-label={t('messageLabel')}
          placeholder={t('placeholder')} />
        <button type="submit" className="btn btn-sm btn-purple" aria-label={t('send')}
          disabled={!input.trim() || ask.isPending}><Icon name="send" size={16} /></button>
      </form>
    </>
  );
}

// ── Alertas — a MESMA caixa inteligente da página /notificacoes (useInbox: mesma
// query, mesma cache, mesmos cartões). Aqui em forma compacta: já triada pelo
// Hexis (score), sem faixas — o overlay é uma espreitadela, não a caixa toda. ──
function AlertasView() {
  const { t } = useTranslation('hexis');
  const { inbox, open, readAll } = useInbox();
  const data = inbox.data;

  if (inbox.isLoading) return <div className="sheet-scroll"><div className="loading-state" role="status">{t('triaging')}</div></div>;

  // O cérebro É a caixa. Em baixo → estado honesto, nunca uma lista a fingir.
  if (!data || !data.hexis.online) {
    return (
      <div className="sheet-scroll">
        <EmptyState compact icon="hexis" title={t('unavailable')}
          subtitle={t('unavailableSub')} />
      </div>
    );
  }

  const { lanes, counts, briefing } = data;
  // Já vêm ordenados por relevância — o que interessa primeiro, primeiro.
  const list = [...lanes.agir_agora, ...lanes.para_ti, ...lanes.historico];

  if (!list.length) {
    return (
      <div className="sheet-scroll">
        <EmptyState compact icon="hexis" title={t('clean')}
          subtitle={t('cleanSub')} />
      </div>
    );
  }

  return (
    <div className="sheet-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative', zIndex: 1 }}>
      {briefing && <div style={{ padding: '12px var(--gutter) 0' }}><Briefing briefing={briefing} compact /></div>}
      <div style={{ padding: '12px var(--gutter) 0' }}>
        <InboxStatus unread={counts.unread} onReadAll={() => readAll.mutate()} pending={readAll.isPending} />
      </div>
      <div className="stack tight" style={{ padding: '0 var(--gutter) 20px' }}>
        {list.map((n) => <NotifCard key={n.id} n={n} onOpen={open} />)}
      </div>
    </div>
  );
}
